import axios, {AxiosError, AxiosResponse} from 'axios';

// Create a base Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // Replace with your API's base URL
  timeout: 120000,
});

// Error message mapping for different HTTP status codes
const getErrorMessage = (status: number, defaultMessage: string): string => {
  const errorMessages: { [key: number]: string } = {
    400: 'Bad Request - Please check your input and try again',
    401: 'Unauthorized - Please log in again',
    403: 'Forbidden - You don\'t have permission to access this resource',
    404: 'Not Found - The requested resource was not found',
    409: 'Conflict - This resource already exists',
    422: 'Validation Error - Please check your input',
    429: 'Too Many Requests - Please wait a moment and try again',
    500: 'Internal Server Error - Please try again later',
    502: 'Bad Gateway - Service temporarily unavailable',
    503: 'Service Unavailable - Please try again later',
    504: 'Gateway Timeout - Request timed out',
  };
  
  return errorMessages[status] || defaultMessage;
};

// Get error context for better error handling
const getErrorContext = (error: AxiosError) => {
  const { response, request, message, code } = error;
  
  if (response) {
    // Server responded with error status
    const { status, data } = response;
    const responseData = data as any; // Type assertion for dynamic data
    return {
      type: 'api_error',
      status,
      message: responseData?.message || getErrorMessage(status, 'An error occurred'),
      details: responseData?.details || responseData,
    };
  } else if (request) {
    // Request was made but no response received
    return {
      type: 'network_error',
      message: 'Network error - Please check your connection',
      details: 'No response received from server',
    };
  } else {
    // Something else happened
    return {
      type: 'unknown_error',
      message: message || 'An unexpected error occurred',
      details: code || 'Unknown error code',
    };
  }
};

// Add an interceptor to attach JWT token to headers
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage (or any other storage you're using)
    const token = localStorage.getItem('access_token');

    // If token exists, add it to the Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    // Handle errors in the request
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Any status code within the range of 2xx causes this function to trigger
    return response;
  },
  (error: AxiosError) => {
    const errorContext = getErrorContext(error);
    
    // Handle authentication errors (401, 403)
    if (errorContext.type === 'api_error' && errorContext.status && [401, 403].includes(errorContext.status)) {
      // Clear authentication data
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Return the error so components can handle it if needed
    return Promise.reject({
      ...error,
      errorContext,
    });
  }
);

export default axiosInstance;